/**
 * Table Export Utilities for Amtrend Command Center (Trendy)
 * Supports: Clipboard (tab-separated), CSV, Excel (.xlsx), PDF
 * Excel and PDF use dynamic imports to keep the main bundle small.
 */

// ---------------------------------------------------------------------------
// Clipboard (tab-separated — pastes cleanly into Excel / Google Sheets)
// ---------------------------------------------------------------------------

export function copyTableToClipboard(headers, rows) {
  const tsv = [
    headers.join('\t'),
    ...rows.map(row => row.join('\t'))
  ].join('\n')
  return navigator.clipboard.writeText(tsv)
}

// ---------------------------------------------------------------------------
// CSV Download (pure JS — no library needed)
// ---------------------------------------------------------------------------

export function downloadCSV(headers, rows, filename = 'trendy-export.csv') {
  const escapeCSV = (val) => {
    const str = String(val ?? '')
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ].join('\n')

  // BOM prefix (\uFEFF) ensures Excel reads UTF-8 correctly on Windows
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, filename)
}

// ---------------------------------------------------------------------------
// Excel Download (dynamic import — xlsx loaded only on click)
// ---------------------------------------------------------------------------

export async function downloadExcel(headers, rows, filename = 'trendy-export.xlsx') {
  const XLSX = await import('xlsx')

  const wsData = [headers, ...rows]
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Auto-size columns based on content length
  ws['!cols'] = headers.map((h, i) => {
    const maxLen = Math.max(
      String(h).length,
      ...rows.map(r => String(r[i] ?? '').length)
    )
    return { wch: Math.min(maxLen + 2, 50) }
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Data')

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  triggerDownload(blob, filename)
}

// ---------------------------------------------------------------------------
// PDF Download (dynamic import — jspdf loaded only on click)
// ---------------------------------------------------------------------------

export async function downloadPDF(headers, rows, filename = 'trendy-export.pdf') {
  // Import autoTable as a direct function — avoids prototype patching issues
  // that break with Vite dynamic imports (doc.autoTable won't exist otherwise)
  const { default: jsPDF } = await import('jspdf')
  const { default: autoTable } = await import('jspdf-autotable')

  const doc = new jsPDF({
    orientation: headers.length > 5 ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4'
  })

  // Amtrend branding
  doc.setFontSize(11)
  doc.setTextColor(0, 114, 206) // #0072CE amtrend-blue
  doc.text('Amtrend Command Center', 14, 15)

  doc.setFontSize(8)
  doc.setTextColor(128, 128, 128)
  doc.text(`Exported ${new Date().toLocaleString()}`, 14, 20)

  // Table — call autoTable as function (not method on doc)
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 25,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [0, 114, 206], // amtrend-blue
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    margin: { top: 25, left: 14, right: 14 },
  })

  doc.save(filename)
}

// ---------------------------------------------------------------------------
// Helper: trigger browser download from Blob
// ---------------------------------------------------------------------------

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
